import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import * as Sentry from '@sentry/react'
import { getSavedPaidOrders, removeOrderFromSavedPaidOrders } from '@utils/carpaySavedPaidOrders.utils'

const filteredOutErrors = ['ORDER_NOT_FOUND', 'SCANNED_CODE_USED', 'ORDER_FREE', 'SCANNED_CODE_INVALID']

export const generateClient = async () => {
    // Create HTTP Link
    const httpLink = new HttpLink({
        uri: process.env.VITE_HASURA_URL
    })

    // Create Retry Link
    const retryLink = new RetryLink({ attempts: { max: Infinity } })

    // Create Auth Link
    const authLink = setContext((_, { headers }) => {
        return {
            headers: {
                ...headers,
                'content-type': 'application/json'
            }
        }
    })

    const errorLink = onError(({ graphQLErrors, networkError }) => {
        // Sentry methods are safe to be called before initialization
        if (graphQLErrors)
            graphQLErrors.map(({ message }) => {
                // Check if the message does not include any of the filtered out errors
                const shouldCapture = filteredOutErrors.every((error) => !message.includes(error))

                if (shouldCapture) {
                    Sentry.captureMessage(message)
                }
            })
        if (networkError) {
            Sentry.captureException(networkError)
        }
    })

    const link = ApolloLink.from([errorLink, retryLink, authLink, httpLink])

    const cache = new InMemoryCache({
        typePolicies: {
            B2CCarpayOrderView: {
                keyFields: (obj) => {
                    const order = obj?.order as { order_id?: string }
                    return order?.order_id
                }
            },
            Query: {
                fields: {
                    b2c_get_order_by_id: {
                        read(existingData, { args, cache }) {
                            if (!args) return undefined
                            const fragment = cache.readFragment({
                                fragment: OrderResponseFragment,
                                id: cache.identify({
                                    __typename: 'B2CCarpayOrderView',
                                    order: { order_id: args.order_id }
                                })
                            })

                            return fragment ?? undefined
                        }
                    },
                    b2c_get_open_orders: {
                        // We control that the orders we are retrieving are not already saved as paid orders.
                        read(existingData, { args, cache }) {
                            if (!existingData) return undefined

                            const existingOrders = existingData?.orders ?? []
                            const paidOrders = getSavedPaidOrders()

                            const orders = existingOrders.filter((order: { __ref: string }) => {
                                const fragment = cache.readFragment({
                                    fragment: OrderResponseFragment,
                                    id: cache.identify({
                                        __typename: 'B2CCarpayOrderView',
                                        order: { order_id: order.__ref }
                                    })
                                })

                                if (!fragment) return false

                                const orderId = fragment.order.order_id

                                return !paidOrders[orderId]
                            })

                            return { orders }
                        }
                    },
                    b2c_get_completed_orders: {
                        // If errors are read as completed orders and they are marked as paid in session storage, we can clear them
                        read(existingData, { args, cache }) {
                            if (!existingData) return undefined

                            const existingOrders = existingData?.orders ?? []
                            const paidOrders = getSavedPaidOrders()

                            existingOrders.forEach((order: { __ref: string }) => {
                                const fragment = cache.readFragment({
                                    fragment: OrderResponseFragment,
                                    id: cache.identify({
                                        __typename: 'B2CCarpayOrderView',
                                        order: { order_id: order.__ref }
                                    })
                                })

                                if (fragment) {
                                    removeOrderFromSavedPaidOrders(fragment.order.order_id)
                                }
                            })

                            return { existingOrders }
                        }
                    }
                }
            }
        }
    })

    return new ApolloClient({
        cache,
        link
    })
}
